import React, { ReactNode } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  dispatch,
  getRefreshToken,
  updateSearchKey,
  useAppSelector,
  Workspace,
} from '@zonofi/zono-money-store';
import { SideDrawerBase } from '@zonofi/mobile-web-common';
import { CurrentWorkspaceCard } from './current-workspace-card';
import { useAppVersion } from '../app-version';
import { DrawerItems } from '../../types';
import { TouchableOpacity } from '../../common';
import { SVGS } from '../../assets';

export interface DrawerViewProps {
  drawerItems: DrawerItems[];
  /**
   * The active workspace displayed in the drawer header.
   * field-ops shows the current user card instead; pass null to render
   * the userHeaderSlot fallback.
   */
  workspace?: Workspace | null;
  /**
   * Override the entire header left section (e.g. field-ops renders
   * <CurrentUserCard> instead of <CurrentWorkspaceCard>).
   */
  userHeaderSlot?: ReactNode;
  workspacesList: Workspace[];
  onDrawerItemPress: (
    route: string,
    label: string,
    actionType?: string
  ) => void;
  onLogOutPress: () => void;
  onCrossPress?: () => void;
  canInstall?: boolean;
  handleInstall?: () => void;
  onSwitchWorkspace?: () => void;
}

export const DrawerView: React.FC<DrawerViewProps> = ({
  drawerItems,
  workspace = null,
  userHeaderSlot,
  workspacesList,
  onDrawerItemPress,
  onLogOutPress,
  onCrossPress,
  canInstall,
  handleInstall,
  onSwitchWorkspace,
}) => {
  const refreshToken = useAppSelector(getRefreshToken);
  const appVersion = useAppVersion();

  const headerLeft = userHeaderSlot ?? (
    <CurrentWorkspaceCard workspace={workspace} />
  );

  return (
    <React.Fragment>
      <div className="side-drawer-root">
        {/* ── Header row ───────────────────────────────────────────────── */}
        <div className="side-drawer-header">
          {headerLeft}
          <IconButton onClick={onCrossPress} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* ── Switch workspace button ───────────────────────────────────── */}
        {refreshToken && workspacesList?.length > 0 && (
          <TouchableOpacity
            className="side-drawer-workspaces"
            onPress={onSwitchWorkspace}
          >
            <img src={SVGS.Synchronize} alt="" />
            <span className="side-drawer-workspaces--text font-semibold">
              Switch Workspace
            </span>
          </TouchableOpacity>
        )}

        <div className="side-drawer-divider" />

        {/* ── Nav list ─────────────────────────────────────────────────── */}
        <SideDrawerBase.List
          items={drawerItems}
          onItemPress={onDrawerItemPress}
          onClose={() => {
            onCrossPress?.();
            dispatch(updateSearchKey(''));
          }}
        />
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <SideDrawerBase.Footer
        canInstall={canInstall}
        onLogout={onLogOutPress}
        onInstall={handleInstall}
        appVersion={appVersion}
      />
    </React.Fragment>
  );
};
